import { useMemo } from 'react'
import { LiveNotificationRecord, NotificationRecord } from '@src/models/notifications/types'
import { NotificationEngineNotificationTemplateKind } from '@src/types'

export type NotificationInboxAvatarKind = 'brand' | 'coach' | 'nora'
export type NotificationAlertSeverity = 'warning' | 'error'

export interface NotificationInboxPresentation {
  alertSeverity: NotificationAlertSeverity
  avatarKind: NotificationInboxAvatarKind
  isAlert: boolean
  isPromotion: boolean
  isUnread: boolean
  senderLabel: string
  showOfferTag: boolean
}

interface UseNotificationInboxPresentationOptions {
  notification: NotificationPresentationRecord
}

type NotificationPresentationRecord = {
  payload: NotificationRecord['payload'] | LiveNotificationRecord['payload']
  readAt?: NotificationRecord['readAt']
  templateKind: NotificationRecord['templateKind'] | LiveNotificationRecord['templateKind']
}

const getAlertSeverity = (
  payload: NotificationPresentationRecord['payload'],
): NotificationAlertSeverity => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return 'warning'
  }

  const severity = (payload as { severity?: unknown }).severity

  return severity === 'error' ? 'error' : 'warning'
}

export const useNotificationInboxPresentation = ({
  notification,
}: UseNotificationInboxPresentationOptions): NotificationInboxPresentation => {
  return useMemo(() => {
    const alertSeverity = getAlertSeverity(notification.payload)

    switch (notification.templateKind) {
      case NotificationEngineNotificationTemplateKind.Alert:
        return {
          alertSeverity,
          avatarKind: 'brand',
          isAlert: true,
          isPromotion: false,
          isUnread: !notification.readAt,
          senderLabel: 'Nutrisense',
          showOfferTag: false,
        }
      case NotificationEngineNotificationTemplateKind.Coach:
        return {
          alertSeverity: 'warning',
          avatarKind: 'coach',
          isAlert: false,
          isPromotion: false,
          isUnread: !notification.readAt,
          senderLabel: 'Care Team',
          showOfferTag: false,
        }
      case NotificationEngineNotificationTemplateKind.Nora:
        return {
          alertSeverity: 'warning',
          avatarKind: 'nora',
          isAlert: false,
          isPromotion: false,
          isUnread: !notification.readAt,
          senderLabel: 'Nora AI',
          showOfferTag: false,
        }
      case NotificationEngineNotificationTemplateKind.Promotion:
        return {
          alertSeverity: 'warning',
          avatarKind: 'brand',
          isAlert: false,
          isPromotion: true,
          isUnread: !notification.readAt,
          senderLabel: 'Nutrisense',
          showOfferTag: true,
        }
      case NotificationEngineNotificationTemplateKind.Passive:
      case NotificationEngineNotificationTemplateKind.Standard:
      default:
        return {
          alertSeverity: 'warning',
          avatarKind: 'brand',
          isAlert: false,
          isPromotion: false,
          isUnread: !notification.readAt,
          senderLabel: 'Nutrisense',
          showOfferTag: false,
        }
    }
  }, [notification.payload, notification.readAt, notification.templateKind])
}
